import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "lib/utils";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import {
  Link as RouterLink,
  type LinkProps as RouterLinkProps,
} from "react-router-dom";

const linkVariants = cva("link link-hover", {
  variants: {
    color: {
      default: "text-violet-600",
      primary: "text-primary",
      muted: "text-base-content/70",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

type LinkVariantProps = VariantProps<typeof linkVariants>;

type BaseProps = {
  color?: LinkVariantProps["color"];
  children: ReactNode;
};

type InternalProps = BaseProps & {
  to: RouterLinkProps["to"];
  href?: never;
} & Omit<RouterLinkProps, "to" | "className" | "children">;

type ExternalProps = BaseProps & {
  href: string;
  to?: never;
  /** 外部リンクは別タブで開く。false で同タブ */
  newTab?: boolean;
} & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    "href" | "className" | "children" | "target" | "rel"
  >;

export type LinkProps = InternalProps | ExternalProps;

/**
 * 純粋な Link リーフ。内部リンク (to) と外部リンク (href) の両方に対応する。
 * 色はデフォルトで text-violet-600 (CRM タレント列と統一)。
 * className は意図的に受け取らない。
 * @param {LinkProps} props Link の props
 * @return {JSX.Element} Link 要素
 */
export const Link = (props: LinkProps): JSX.Element => {
  const { color, children } = props;
  const classes = cn(linkVariants({ color }));

  if ("href" in props && props.href !== undefined) {
    const { href, newTab = true, ...rest } = props as ExternalProps;
    return (
      <a
        href={href}
        className={classes}
        {...(newTab ? { target: "_blank", rel: "noreferrer noopener" } : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }

  const { to, ...rest } = props as InternalProps;
  return (
    <RouterLink to={to} className={classes} {...rest}>
      {children}
    </RouterLink>
  );
};
